import { localStorageSize, stringSize } from "@/utils/estimateMemory.ts";
import { generateWordId } from "@/utils/generatePronouncableId.ts";
import * as Sentry from "@sentry/vue";
import { DateTime } from "luxon";
import type { AppConfig } from "vue";

interface ContextBase {
  contextType: string;
}

export interface RequestContext extends ContextBase {
  contextType: "request";
  requestInit: RequestInit;
  url: string;
}

const REDACTED = "REDACTED";

export function sanitizeHeader(header: HeadersInit): HeadersInit {
  const _header = { ...header };
  if (_header["Authorization"] !== undefined) {
    if (String(_header["Authorization"]).startsWith("Bearer")) {
      _header["Authorization"] = "Bearer REDACTED";
    } else {
      _header["Authorization"] = REDACTED;
    }
  }
  return _header;
}

export function createRequestContextFromRequest(
  url: string,
  requestInit: RequestInit,
): RequestContext {
  const req = new Request(url, requestInit);
  const result: RequestContext = {
    contextType: "request",
    url,
    requestInit: {
      ...requestInit,
      method: req.method,
      referrer: req.referrer,
      referrerPolicy: req.referrerPolicy,
      mode: req.mode,
      credentials: req.credentials,
      cache: req.cache,
      redirect: req.redirect,
      integrity: req.integrity,
      keepalive: req.keepalive,
    },
  };

  if (result?.requestInit?.headers != undefined) {
    result.requestInit.headers = sanitizeHeader(result.requestInit.headers);
  }

  return result;
}

export interface ResponseContext extends ContextBase {
  contextType: "response";
  ok: boolean;
  status: number;
  statusText: string;
  url: string;
  headers: HeadersInit;
  type: ResponseType;
  redirected: boolean;
  bodyUsed: boolean;
  body?: undefined | string;
}

export async function createResponseContextFromResponse(
  response: Response,
): Promise<ResponseContext> {
  const result: ResponseContext = {
    contextType: "response",
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    url: response.url,
    headers: Array.from(response.headers.entries()),
    type: response.type,
    bodyUsed: response.bodyUsed,
    redirected: response.redirected,
  };

  if (!response.bodyUsed) {
    try {
      result.body = await response.text();
    } catch {
      // ignore
    }
  }

  result.headers = sanitizeHeader(result.headers);

  return result;
}

export interface ErrorContext extends ContextBase {
  contextType: "error";
  message: string;
  name: string | undefined;
  stack: string | undefined;
  columnNumber: number | undefined;
  lineNumber: number | undefined;
}

function createErrorContextFromError(error: Error): ErrorContext {
  return {
    contextType: "error",
    message: error?.message,
    name: error?.name,
    stack: error?.stack,
    //@ts-expect-error generally defined by major browsers
    columnNumber: error?.columnNumber,
    //@ts-expect-error generally defined by major browsers
    lineNumber: error?.lineNumber,
  };
}

export function createUnstructuredErrorContext(
  value: any,
): UnstructuredContext {
  return {
    contextType: "unstructured",
    value: value,
  };
}

export interface AppInfoContext extends ContextBase {
  contextType: "appInfo";
  version: string;
  backend: string;
}

export interface BrowserContext extends ContextBase {
  contextType: "browser";
  name: string;
  version: string | number;
  os: string | undefined;
  userAgent: string;
}

export interface UnstructuredContext extends ContextBase {
  contextType: "unstructured";
  value: any;
}

export interface UserContext extends ContextBase {
  contextType: "user";
  user: string;
}

export class MessageContext implements ContextBase {
  contextType: "message";
  message: string;

  constructor(message: string) {
    this.contextType = "message";
    this.message = message;
  }
}

export interface VueErrorContext extends ContextBase {
  contextType: "vueError";
  /** vue error codes: https://vuejs.org/error-reference/#runtime-errors */
  info: string;
}

export type Context =
  | RequestContext
  | ResponseContext
  | ErrorContext
  | AppInfoContext
  | BrowserContext
  | UnstructuredContext
  | MessageContext
  | UserContext
  | VueErrorContext;

export class LoggableError extends Error {
  context: Context[];
  public identifier: string;

  constructor(
    context: Context | Context[],
    options: {
      previousError?: unknown;
      identifier?: string;
    } = {},
  ) {
    super();
    this.name = "LogError";
    this.identifier = options?.identifier ?? generateWordId();

    if (Array.isArray(context)) {
      this.context = context;
    } else {
      this.context = [context];
    }

    const messageCtx = this.context.find(
      (ctx) => ctx.contextType === "message",
    );
    if ((messageCtx as MessageContext)?.message != undefined) {
      this.message = (messageCtx as MessageContext).message;
    }

    if (options.previousError instanceof Error) {
      this.cause = options.previousError;
    }
  }

  public getContext(): Context[] {
    let contextList: Context[] = this.context;

    let previous: unknown = (this?.cause as any) ?? null;
    while (previous != null) {
      if (previous instanceof LoggableError) {
        contextList = contextList.concat(previous.context);
      } else if (previous instanceof Error) {
        contextList.push(createErrorContextFromError(previous));
      }
      previous = (previous as any)?.cause ?? null;
    }

    return contextList.reverse();
  }

  public getOriginError(): Error | null {
    let prev = this.cause;
    while (prev) {
      if (prev instanceof Error && !(prev instanceof LoggableError)) {
        return prev;
      } else {
        prev = (prev as any)?.cause;
      }
    }
    return null;
  }
}

export class LoggableMessageError extends LoggableError {
  constructor(message: string, previous: unknown = null) {
    super(
      {
        contextType: "message",
        message,
      },
      { previousError: previous },
    );
  }
}

type LogSubscriber = (newEntries: LogEntry[]) => void;
export const LogLocalStorageKey = "log";
export const LOCAL_STORAGE_MAX_ENTRIES = 200;
/** If log is larger than MAX_ENTRIES, only the last 150 entries are kept. */
export const LOCAL_STORAGE_SOFT_MAX_ENTRIES = 150;
export const LOCAL_STORAGE_MAX_AGE = 31 * 24 * 60 * 60 * 1000; // 31 days
export enum SEVERITY {
  "INFO",
  "WARNING",
  "ERROR",
}

export interface LogEntry {
  identifier?: string;
  timestamp: number; // unix time
  severity: SEVERITY;
  context: Context[];
}

export class Log {
  subscribers: Set<LogSubscriber> = new Set();
  /** sorted timestamp: old -> new */
  private entries: LogEntry[] = [];

  constructor(entries: LogEntry[] = []) {
    entries.sort((a, b) => a.timestamp - b.timestamp);
    this.setEntries(entries);
  }

  addEntry(logEntry: LogEntry): void {
    if (this.entries.length === 0) {
      this.entries.push(logEntry);
    } else if (this.entries.length === 1) {
      if (this.entries[0].timestamp > logEntry.timestamp) {
        this.entries.unshift(logEntry);
      } else {
        this.entries.push(logEntry);
      }
    } else if (this.entries.at(-1)!.timestamp < logEntry.timestamp) {
      // is newest
      this.entries.push(logEntry);
    } else if (this.entries.at(0)!.timestamp > logEntry.timestamp) {
      // is oldest
      this.entries.unshift(logEntry);
    } else {
      // is in the middle
      const index = this.entries.findLastIndex(
        (x) => x.timestamp < logEntry.timestamp,
      );
      if (index !== -1) {
        this.entries.splice(index, 0, logEntry);
      } else {
        this.entries.push(logEntry);
      }
    }
    this.notifySubscribers([logEntry]);
    this.cleanup();
  }

  addError(error: unknown, options: { severity?: SEVERITY } = {}): void {
    if (error instanceof LoggableError) {
      this.fromLoggableError(error, options);
    } else if (error instanceof Error) {
      if (error.name === "AbortError") {
        this.wrapContext([createErrorContextFromError(error)], {
          severity: options?.severity ?? SEVERITY.INFO,
        });
      } else {
        this.wrapContext([createErrorContextFromError(error)], options);
      }
    }
  }

  getEntries(): LogEntry[] {
    return this.entries;
  }

  info(message: string) {
    this.addEntry({
      timestamp: DateTime.now().toMillis(),
      severity: SEVERITY.INFO,
      context: [
        {
          contextType: "message",
          message,
        },
      ],
    });
  }

  setEntries(entries: LogEntry[]): void {
    entries.sort((a, b) => a.timestamp - b.timestamp);
    this.entries = entries;
  }

  subscribe(subscriber: LogSubscriber): void {
    this.subscribers.add(subscriber);
  }

  unsubscribe(subscriber: LogSubscriber): void {
    this.subscribers.delete(subscriber);
  }

  warn(message: string) {
    this.addEntry({
      timestamp: DateTime.now().toMillis(),
      severity: SEVERITY.WARNING,
      context: [
        {
          contextType: "message",
          message,
        },
      ],
    });
  }

  private fromLoggableError(
    error: LoggableError,
    options: { severity?: SEVERITY } = {},
  ): void {
    const logEntry: LogEntry = {
      timestamp: DateTime.now().toMillis(),
      severity: options?.severity ?? SEVERITY.ERROR,
      identifier: error.identifier,
      context: error.getContext(),
    };
    this.addEntry(logEntry);
  }

  private fromError(error: Error): void {
    this.wrapContext([createErrorContextFromError(error)]);
  }

  private notifySubscribers(newEntries: LogEntry[]): void {
    this.subscribers.forEach((subscriber) => subscriber(newEntries));
  }

  private wrapContext(
    context: Context[],
    options: {
      severity?: SEVERITY;
    } = {},
  ): void {
    const logEntry: LogEntry = {
      timestamp: DateTime.now().toMillis(),
      severity: options?.severity ?? SEVERITY.ERROR,
      context,
    };
    this.addEntry(logEntry);
  }

  private cleanup(): void {
    if (this.entries.length === 0) return;
    const beforeCount = this.entries.length;

    if (this.entries.length > LOCAL_STORAGE_MAX_ENTRIES) {
      this.entries.splice(
        0,
        this.entries.length -
          Math.min(LOCAL_STORAGE_SOFT_MAX_ENTRIES, LOCAL_STORAGE_MAX_ENTRIES),
      );
    }

    const now = DateTime.now().toMillis();
    if (this.entries[0].timestamp < now - LOCAL_STORAGE_MAX_AGE) {
      const oldestAllowedIndex = this.entries.findIndex(
        (x) => x.timestamp < now - LOCAL_STORAGE_MAX_AGE,
      );
      if (oldestAllowedIndex !== -1) {
        this.entries.splice(
          oldestAllowedIndex,
          this.entries.length - oldestAllowedIndex,
        );
      } else {
        this.entries.splice(0, this.entries.length);
      }
    }

    const afterCount = this.entries.length;

    if (afterCount < beforeCount) {
      log.addEntry({
        timestamp: DateTime.now().toMillis(),
        severity: SEVERITY.INFO,
        context: [
          {
            contextType: "message",
            message: `Log entries removed: ${beforeCount - afterCount}`,
          },
        ],
      });
    }
  }
}

export function handleLocalStorageQuotaError(
  error: DOMException,
  log: Log,
  storage: Storage,
  sentry: typeof Sentry,
): void {
  const storageSize = localStorageSize();
  const logSize = stringSize(JSON.stringify(log.getEntries()));
  const logEntryCount = log.getEntries().length;

  sentry.captureException(error, {
    extra: {
      localStorageKeys: Object.keys(storage),
      logSize: logSize.toString(),
      logEntryCount: logEntryCount.toString(),
      totalStorageSize: Object.entries(storageSize)
        .map(([key, size]) => `${key} ${size}`)
        .join(", "),
    },
  });
}

export const localStorageLogSubscriber: LogSubscriber = () => {
  try {
    localStorage.setItem(LogLocalStorageKey, JSON.stringify(log.getEntries()));
  } catch (e) {
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      handleLocalStorageQuotaError(e, log, localStorage, Sentry);
    } else {
      throw e;
    }
  }
};

export const consoleLogSubscriber: LogSubscriber = (entries) => {
  entries.forEach((entry: any) => {
    // eslint-disable-next-line no-console -- this is the feature of this function
    if (entry?.message) console.error(entry!.message);
    for (const ctx of entry.context) {
      if (ctx.contextType === "error") {
        const err = new Error(ctx.message);
        err.stack = ctx.stack;
        // eslint-disable-next-line no-console -- this is the feature of this function
        console.error(err);
      } else {
        // eslint-disable-next-line no-console -- this is the feature of this function
        console.error(ctx);
      }
    }
  });
};

// APP Log
export const log = new Log();

try {
  const localEntries = JSON.parse(localStorage.getItem("log") ?? "[]");
  if (Array.isArray(localEntries)) {
    log.setEntries(localEntries);
  }
} catch {
  // ignore
}

log.subscribe(localStorageLogSubscriber);
log.subscribe((entries) => {
  for (const entry of entries) {
    entry.context.forEach((ctx) => {
      Sentry.setContext(ctx.contextType, ctx as unknown as any);
    });

    const errorContext = entry.context.find((x) => x.contextType === "error");
    if (errorContext) {
      let errorObject = new Error(errorContext.message);
      errorObject = Object.assign(errorObject, errorContext);

      Sentry.captureException(errorObject, {
        level: severityToSentryLevel(entry.severity),
      });
      return;
    }

    const messageContext = entry.context.find(
      (x) => x.contextType === "message",
    );
    if (messageContext) {
      Sentry.captureMessage(messageContext.message, {
        level: severityToSentryLevel(entry.severity),
      });
      return;
    }

    Sentry.captureMessage(`Log (${SEVERITY[entry.severity]})`, {
      level: severityToSentryLevel(entry.severity),
    });
  }
});

function severityToSentryLevel(severity: SEVERITY) {
  switch (severity) {
    case SEVERITY.INFO:
      return "info";
    case SEVERITY.WARNING:
      return "warning";
    case SEVERITY.ERROR:
    default:
      return "error";
  }
}

export const logVueErrorHandler: AppConfig["errorHandler"] = (
  err,
  instance,
  info,
) => {
  if (err instanceof LoggableError) {
    err.context.push({
      contextType: "vueError",
      info,
    });
  }

  log.addError(err);
};
